import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Search, Monitor, Ticket, Building, Mail, Phone,
  ChevronRight, Plus, RefreshCw, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const UserDirectoryPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  
  // Add user modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    display_name: '',
    first_name: '',
    last_name: '',
    department: ''
  });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [search, departmentFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.getUserDirectory({ 
        search, 
        department: departmentFilter === 'all' ? '' : departmentFilter,
        limit: 200 
      });
      setUsers(res.data.users || []);
      setDepartments(res.data.departments || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.display_name) {
      toast.error('Email and name are required');
      return;
    }
    
    setAdding(true);
    try {
      await api.createUser({
        email: newUser.email,
        display_name: newUser.display_name,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        department: newUser.department
      });
      toast.success('User added');
      setShowAddModal(false);
      setNewUser({ email: '', display_name: '', first_name: '', last_name: '', department: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add user');
    } finally {
      setAdding(false);
    }
  };

  // Stats
  const totalUsers = users.length;
  const usersWithDevices = users.filter(u => u.assigned_devices > 0).length;
  const usersWithTickets = users.filter(u => u.open_tickets > 0).length;
  const uniqueDepts = new Set(users.map(u => u.department).filter(Boolean)).size;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="user-directory-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-blue-600" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">User Directory</h1>
                <p className="text-sm text-gray-500">Manage employees, devices, and tickets</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/')}>
                Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate('/assets')}>
                Assets
              </Button>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add User
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">With Devices</p>
                  <p className="text-2xl font-bold text-green-600">{usersWithDevices}</p>
                </div>
                <Monitor className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Open Tickets</p>
                  <p className="text-2xl font-bold text-orange-600">{usersWithTickets}</p>
                </div>
                <Ticket className="h-8 w-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Departments</p>
                  <p className="text-2xl font-bold text-purple-600">{uniqueDepts}</p>
                </div>
                <Building className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="user-search-input"
                />
              </div>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-48" data-testid="department-filter">
                  <Filter className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchUsers} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* User List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">
              {loading ? 'Loading...' : `${users.length} Users`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No users found</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {users.map((user) => (
                  <div
                    key={user.email}
                    onClick={() => navigate(`/users/${encodeURIComponent(user.email)}`)}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    data-testid={`user-row-${user.email}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-700 font-medium text-sm">
                          {user.first_name?.charAt(0) || user.display_name?.charAt(0) || '?'}
                          {user.last_name?.charAt(0) || ''}
                        </span>
                      </div>
                      
                      {/* Info */}
                      <div>
                        <p className="font-medium text-gray-900">{user.display_name}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {/* Department */}
                      {user.department && (
                        <Badge variant="outline" className="text-xs">
                          {user.department}
                        </Badge>
                      )}
                      
                      {/* Devices */}
                      <div className="flex items-center gap-1 text-sm text-gray-600 min-w-[80px]">
                        <Monitor className="h-4 w-4 text-gray-400" />
                        <span>{user.assigned_devices || 0} devices</span>
                      </div>
                      
                      {/* Tickets */}
                      <div className="flex items-center gap-1 text-sm min-w-[80px]">
                        <Ticket className={`h-4 w-4 ${user.open_tickets > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
                        <span className={user.open_tickets > 0 ? 'text-orange-600 font-medium' : 'text-gray-600'}>
                          {user.open_tickets || 0} open
                        </span>
                      </div>
                      
                      <ChevronRight className="h-5 w-5 text-gray-300" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add User Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              Add New User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Email *</label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(u => ({ ...u, email: e.target.value }))}
                placeholder="user@gamerpackaging.com"
                data-testid="new-user-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">First Name</label>
                <Input
                  value={newUser.first_name}
                  onChange={(e) => setNewUser(u => ({ ...u, first_name: e.target.value }))}
                  placeholder="John"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Last Name</label>
                <Input
                  value={newUser.last_name}
                  onChange={(e) => setNewUser(u => ({ ...u, last_name: e.target.value }))}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Display Name *</label>
              <Input
                value={newUser.display_name}
                onChange={(e) => setNewUser(u => ({ ...u, display_name: e.target.value }))}
                placeholder="John Doe"
                data-testid="new-user-display-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Department</label>
              <Select 
                value={newUser.department} 
                onValueChange={(val) => setNewUser(u => ({ ...u, department: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                  <SelectItem value="IT">IT</SelectItem>
                  <SelectItem value="Sales">Sales</SelectItem>
                  <SelectItem value="Production">Production</SelectItem>
                  <SelectItem value="Warehouse">Warehouse</SelectItem>
                  <SelectItem value="Finance">Finance</SelectItem>
                  <SelectItem value="HR">HR</SelectItem>
                  <SelectItem value="Executive">Executive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAddUser}
              disabled={adding}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-user-submit"
            >
              {adding ? 'Adding...' : 'Add User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDirectoryPage;
